import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "react-feather";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import styled from "styled-components";
import type { ScheduleRefreshRecord } from "../../../pages/Connectors/api";
import { IconMenuButton } from "./ScheduleHistoryModal.styles";

const MENU_MIN_WIDTH = 220;
const MENU_Z = 5000;

const MenuPanel = styled.ul`
  position: fixed;
  margin: 0;
  padding: ${spacing[2]};
  list-style: none;
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 8px 24px rgba(89, 98, 112, 0.18);
  z-index: ${MENU_Z};
  min-width: ${MENU_MIN_WIDTH}px;
`;

const MenuButton = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: ${spacing[2]} ${spacing[3]};
  border: none;
  border-radius: ${borderRadius.sm};
  background: transparent;
  text-align: left;
  cursor: pointer;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};

  &:hover:not(:disabled) {
    background-color: ${rawColors.structural[100]};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${rawColors.text.lowEmphasis};
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 0;
  }
`;

export interface ScheduleRowActionsMenuProps {
  record: ScheduleRefreshRecord;
  openMenuId: string | null;
  onOpen: (id: string) => void;
  onClose: () => void;
  onEdit: (record: ScheduleRefreshRecord) => void;
  onPauseOrResume: (record: ScheduleRefreshRecord) => void;
  onCancel: (record: ScheduleRefreshRecord) => void;
  onDelete: (record: ScheduleRefreshRecord) => void;
}

const useAlignEndMenuRect = (open: boolean) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const compute = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const width = MENU_MIN_WIDTH;
    const left = Math.max(
      8,
      Math.min(r.right - width, window.innerWidth - width - 8),
    );
    setRect({ top: r.bottom + 4, left, width });
  }, []);

  useEffect(() => {
    if (!open) return;
    compute();
    const handle = () => compute();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, compute]);

  return { triggerRef, rect };
};

export const ScheduleRowActionsMenu: React.FC<ScheduleRowActionsMenuProps> = ({
  record,
  openMenuId,
  onOpen,
  onClose,
  onEdit,
  onPauseOrResume,
  onCancel,
  onDelete,
}) => {
  const open = openMenuId === record.id;
  const { triggerRef, rect } = useAlignEndMenuRect(open);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose, triggerRef]);

  const pauseLabel =
    record.status === "paused" ? "Resume schedule" : "Pause schedule";

  return (
    <>
      <IconMenuButton
        ref={triggerRef}
        type="button"
        aria-label="Schedule actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (open) onClose();
          else onOpen(record.id);
        }}
      >
        <MoreVertical size={18} aria-hidden />
      </IconMenuButton>
      {open &&
        rect &&
        createPortal(
          <MenuPanel
            ref={menuRef}
            role="menu"
            style={{
              top: rect.top,
              left: rect.left,
              minWidth: rect.width,
            }}
          >
            <li role="none">
              <MenuButton
                type="button"
                role="menuitem"
                onClick={() => {
                  onClose();
                  onEdit(record);
                }}
              >
                Edit schedule
              </MenuButton>
            </li>
            <li role="none">
              <MenuButton
                type="button"
                role="menuitem"
                disabled={record.status === "cancelled"}
                onClick={() => {
                  onClose();
                  onPauseOrResume(record);
                }}
              >
                {pauseLabel}
              </MenuButton>
            </li>
            <li role="none">
              <MenuButton
                type="button"
                role="menuitem"
                disabled={record.status === "cancelled"}
                onClick={() => {
                  onClose();
                  onCancel(record);
                }}
              >
                Cancel schedule
              </MenuButton>
            </li>
            <li role="none">
              <MenuButton
                type="button"
                role="menuitem"
                onClick={() => {
                  onClose();
                  onDelete(record);
                }}
              >
                Delete schedule
              </MenuButton>
            </li>
          </MenuPanel>,
          document.body,
        )}
    </>
  );
};
